
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Course, GameDurationType } from '@/types';

interface GameFormHeaderProps {
  name: string;
  setName: (name: string) => void;
  courseId: string;
  setCourseId: (courseId: string) => void;
  bet: string;
  setBet: (bet: string) => void;
  durationType: GameDurationType;
  setDurationType: (type: GameDurationType) => void;
  isRecurring: boolean;
  setIsRecurring: (value: boolean) => void;
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string | null;
  setEndDate: (date: string | null) => void;
  courses: Course[];
  isSubmitting: boolean;
  isLoadingCourses: boolean;
  nameError: string;
  courseError: string;
}

const GameFormHeader: React.FC<GameFormHeaderProps> = ({
  name,
  setName,
  courseId,
  setCourseId,
  bet,
  setBet,
  durationType,
  setDurationType,
  isRecurring,
  setIsRecurring,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  courses,
  isSubmitting,
  isLoadingCourses,
  nameError,
  courseError
}) => {
  return (
    <div className="space-y-4 mb-6">
      <div className="space-y-2">
        <Label htmlFor="name">Game Name</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Summer Birdie Challenge"
          disabled={isSubmitting}
        />
        {nameError && <p className="text-destructive text-xs mt-1">{nameError}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="course">Golf Course</Label>
        <Select
          value={courseId}
          onValueChange={setCourseId}
          disabled={isSubmitting || isLoadingCourses}
        >
          <SelectTrigger id="course">
            <SelectValue placeholder="Select a course" />
          </SelectTrigger>
          <SelectContent>
            {isLoadingCourses ? (
              <SelectItem value="loading" disabled>Loading courses...</SelectItem>
            ) : courses.length === 0 ? (
              <SelectItem value="none" disabled>No courses available</SelectItem>
            ) : (
              courses.map((course) => (
                <SelectItem key={course.id} value={course.id}>
                  {course.name}{course.location ? ` — ${course.location}` : ''}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        {courseError && <p className="text-destructive text-xs mt-1">{courseError}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="bet">Bet (optional)</Label>
        <Input
          id="bet"
          value={bet}
          onChange={(e) => setBet(e.target.value)}
          placeholder="e.g., $10 per birdie"
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="durationType">Duration</Label>
        <Select
          value={durationType}
          onValueChange={(value) => setDurationType(value as GameDurationType)}
          disabled={isSubmitting}
        >
          <SelectTrigger id="durationType">
            <SelectValue placeholder="Select duration type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open_ended">Open-Ended</SelectItem>
            <SelectItem value="yearly">Yearly</SelectItem>
            <SelectItem value="custom">Custom Date Range</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {durationType === 'yearly' && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isRecurring"
            checked={isRecurring}
            onCheckedChange={(checked) => setIsRecurring(checked === true)}
            disabled={isSubmitting}
          />
          <Label
            htmlFor="isRecurring"
            className="font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Recurring yearly (auto-creates next year's game)
          </Label>
        </div>
      )}

      {durationType === 'custom' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={startDate.split('T')[0]}
              onChange={(e) => setStartDate(`${e.target.value}T00:00:00.000Z`)}
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={endDate?.split('T')[0] || ''}
              onChange={(e) => setEndDate(e.target.value ? `${e.target.value}T23:59:59.999Z` : null)}
              disabled={isSubmitting}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GameFormHeader;
